// src/data/claimAck.js
//
// BOB, 2026-10-25: "Creating this way forces the claimant to acknowledge their data remains accurate
// and constant because the claimant must acknowledge their information. Click accept, No change, Edit,
// but you get the drift."
//
// WHAT THIS IS FOR, in his terms: a pop-up that a man has to open and answer turns stale data into a
// decision. Before this, a claim carried whatever the profile happened to hold and one boolean
// (`profileConfirmed`) set by a single button on a profile modal - so a wrong roster number or a moved
// address travelled onto the claim with nobody having looked at it. Now each section is answered:
//
//   ACCEPTED  "this is correct, no change"  - he read it and stands behind it
//   EDITED    "I changed it"                - he read it, corrected it, and stands behind that
//
// Either way the claim records WHICH section, WHICH answer and WHEN, so a year later the record shows
// the claimant confirmed his own identity data at filing. That is worth more than the data itself when
// a carrier argues the claim names the wrong man.
//
// WHAT IT DOES NOT DO: it does not judge the data. "Accepted" means he said it was right, not that the
// platform agrees, and nothing here checks a roster number against a roster (that is the seniority
// cross-reference, a different thing, and it belongs to the committee).
//
// Pure. Proved by scripts\test-claim-ack.mjs.

import { ALL_SECTIONS, ALL_SECTION_KEYS, sectionFor, sectionState } from './claimSections';

export const ACK_ACCEPTED = 'accepted';
export const ACK_EDITED = 'edited';

// One field on the claim per section: ackPersonal, ackEmployer, ackUnion, ackHappened, ...
export function ackField(sectionKey) {
  const s = String(sectionKey || '');
  if (!s) return '';
  return 'ack' + s.charAt(0).toUpperCase() + s.slice(1);
}

const whenWords = (at) => {
  if (!at) return '';
  const d = new Date(at);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// The acknowledgement on a form for one section: { answered, action, at, words }
export function ackState(form, sectionKey) {
  const s = sectionFor(sectionKey);
  if (!s) return null;
  const raw = form ? form[ackField(sectionKey)] : null;
  const rec = raw && typeof raw === 'object' ? raw : null;
  const action = rec && (rec.action === ACK_ACCEPTED || rec.action === ACK_EDITED) ? rec.action : '';
  const at = rec && rec.at ? rec.at : '';
  const when = whenWords(at);
  return {
    key: s.key,
    answered: !!action,
    action,
    at,
    words: !action
      ? 'Not yet confirmed - open it and say whether it is right'
      : (action === ACK_ACCEPTED
        ? 'You confirmed this was correct' + (when ? ' on ' + when : '')
        : 'You corrected this and confirmed it' + (when ? ' on ' + when : '')),
  };
}

// The patch to merge into the form when he answers. `now` is injected so this stays pure and a test
// can pin the clock; the caller passes new Date().toISOString().
export function acknowledgePatch(sectionKey, action, now, by) {
  const s = sectionFor(sectionKey);
  if (!s) return {};
  if (action !== ACK_ACCEPTED && action !== ACK_EDITED) return {};
  const rec = { action, at: String(now || ''), by: String(by || '') };
  const out = {};
  out[ackField(sectionKey)] = rec;
  return out;
}

// An EDIT un-answers the section: if a man changes a field after confirming it, his confirmation was
// about the old value. The screen asks again rather than carrying a confirmation of something else.
export function clearOnEdit(form, sectionKey) {
  const f = ackField(sectionKey);
  if (!form || !form[f]) return {};
  const out = {};
  out[f] = null;
  return out;
}

// Which section a field belongs to - so a keystroke can un-answer the right card and only that one.
export function sectionOfField(fieldKey) {
  for (const s of ALL_SECTIONS) if (s.fields.indexOf(fieldKey) !== -1) return s.key;
  return '';
}

// THE GATE. Every section that has something in it to confirm must be answered before the claim is
// filed. A section with no fields at all (exhibits) is still answered, because "I have dealt with my
// attachments" is exactly the thing a claimant forgets.
export function ackGate(form) {
  const missing = [];
  for (const k of ALL_SECTION_KEYS) {
    const st = ackState(form, k);
    if (!st.answered) missing.push({ key: k, title: sectionFor(k).title });
  }
  return { ok: missing.length === 0, missing, text: gateText(missing) };
}

function gateText(missing) {
  if (!missing.length) return 'Every section is confirmed.';
  if (missing.length === 1) return 'One section is not confirmed yet: ' + missing[0].title + '.';
  return missing.length + ' sections are not confirmed yet: ' + missing.map((m) => m.title).join(', ') + '.';
}

// What goes onto the claim itself: one row per section, flat and readable a year later. Nothing is
// written for a section he never answered - an absent acknowledgement is not a denial, and the gate
// above is what stops the filing, not a false row here.
export function ackRecord(form) {
  const out = {};
  for (const k of ALL_SECTION_KEYS) {
    const st = ackState(form, k);
    if (st.answered) out[k] = { action: st.action, at: st.at };
  }
  return out;
}

// The one line above the cards: what is left to do, in plain words.
export function ackSummary(form) {
  const gate = ackGate(form);
  const states = ALL_SECTION_KEYS.map((k) => sectionState(form, k));
  const needsWork = states.filter((st) => st.missingRequired.length);
  if (needsWork.length) {
    return 'Open each card. ' + needsWork.length + ' still need' + (needsWork.length === 1 ? 's' : '')
      + ' something before you can file: '
      + needsWork.map((st) => st.title).join(', ') + '.';
  }
  if (!gate.ok) return 'Everything required is in. ' + gate.text + ' Open it and confirm.';
  return 'Everything is in and confirmed. You can file this claim.';
}
